import { normalize } from 'normalizr';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { xhrStatusFactory } from '../reusable/xhrStatus';
import { withXhrStatusReducer } from './integrations.actions';
import type { IntegrationsState } from './integrations.types';
import {
  CurrentImportStatus,
  disableNextStep,
  enableNextStep,
  fetchedImportStatus,
  fetchedIntegrationsAction,
  fetchedProjectsForWorkspace,
  integrationsListSchema,
  resetImportState,
  resetProjectsSearch,
  searchProjects,
  selectImportAccountId,
  selectIntegrationWorkspace,
  setImportingFrom,
  submittedImportData,
  toggleUnassignedUsersModal,
  withBatchSelectionsReducer,
  workspaceProjectsListSchema,
} from './integrations.types';

export const initialState: IntegrationsState = {
  xhrStatus: xhrStatusFactory(),
  entities: { integrations: {} },
  importAccountId: null,
  selectedWorkspace: null,
  showUnassignedUsersModal: false,
  result: [],
  projects: { query: '', entities: {}, result: [] },
  import: {
    inProgress: false,
    accountId: null,
    importingFrom: [],
    importSuccessFrom: [],
    status: [
      {
        id: 0,
        created_at: '',
        finished_at: null,
        updated_at: '',
        status: 'created',
        details: null,
      },
    ],
  },
  selection: {
    projectIds: [],
  },
  // should come from backend?
  stepEntities: {
    Setup: { name: 'Setup', enabled: true, number: 1 },
    'Projects & Clients': { name: 'Projects & Clients', enabled: false, number: 2 },
    Users: { name: 'Users', enabled: false, number: 3 },
    Tags: { name: 'Tags', enabled: false, number: 4 },
    Finish: { name: 'Finish', enabled: false, number: 5 },
  },
  steps: ['Setup', 'Projects & Clients', 'Users', 'Tags', 'Finish'],
};

const reducer = reducerWithInitialState(initialState)
  .case(fetchedIntegrationsAction, (state, payload) => {
    if (!payload) {
      return state;
    }
    const { entities, result } = normalize<typeof payload, IntegrationsState['entities']>(
      payload,
      integrationsListSchema
    );

    // if we have workspaces, select the first one unless we have already made a selection
    const firstWorkspace = result.length > 0 ? result[0] : null;
    const selectedWorkspace =
      state.selectedWorkspace != null ? state.selectedWorkspace : firstWorkspace;
    return {
      ...state,
      entities,
      result,
      selectedWorkspace,
    };
  })
  .case(selectIntegrationWorkspace, (state, payload) => {
    if (payload.id == null) {
      return {
        ...state,
        projects: initialState.projects,
        selectedWorkspace: payload.id,
        stepEntities: initialState.stepEntities,
      };
    }

    return {
      ...state,
      projects: initialState.projects,
      selectedWorkspace: payload.id,
    };
  })
  .case(enableNextStep, (state, payload) => {
    const currentStep = state.stepEntities[payload.currentStep];
    const stepToEnable = state.steps[currentStep.number];
    const stepObj = state.stepEntities[stepToEnable];
    return {
      ...state,
      stepEntities: {
        ...state.stepEntities,
        [stepObj.name]: {
          ...stepObj,
          enabled: true,
        },
      },
    };
  })
  .case(disableNextStep, (state, payload) => {
    const currentStep = state.stepEntities[payload.currentStep];
    const stepToDisable = state.steps[currentStep.number];
    const { [stepToDisable]: disable, ...rest } = state.stepEntities;

    return {
      ...state,
      stepEntities: {
        ...rest,
        [disable.name]: {
          ...disable,
          enabled: false,
        },
      },
    };
  })
  .case(fetchedProjectsForWorkspace, (state, payload) => {
    if (payload.length === 0 || !payload) {
      return {
        ...state,
        projects: { entities: {}, result: [], query: state.projects.query },
      };
    }
    const { entities, result } = normalize(payload, workspaceProjectsListSchema);

    if (!entities.workspace_projects) {
      return state;
    }

    return {
      ...state,
      projects: { entities: entities.workspace_projects, result, query: state.projects.query },
    };
  })
  .case(searchProjects, (state, payload) => {
    return {
      ...state,
      projects: {
        ...state.projects,
        query: payload,
      },
    };
  })
  .case(resetProjectsSearch, state => {
    return {
      ...state,
      projects: {
        ...state.projects,
        query: '',
      },
    };
  })
  .case(submittedImportData, (state, { accountId }) => {
    return {
      ...initialState,
      import: {
        ...initialState.import,
        importingFrom: state.import.importingFrom,
        importSuccessFrom: state.import.importSuccessFrom,
        accountId,
        inProgress: true,
      },
    };
  })
  .case(setImportingFrom, (state, { accountId }) => {
    return {
      ...state,
      import: {
        ...state.import,
        importingFrom: [...state.import.importingFrom, accountId],
        importSuccessFrom: state.import.importingFrom.filter(id => id !== accountId),
      },
    };
  })
  .case(fetchedImportStatus, (state, { status, accountId }) => {
    const lastItem = status[status.length - 1];
    if (lastItem?.status === CurrentImportStatus.finished) {
      return {
        ...state,
        import: {
          ...state.import,
          status,
          importingFrom: state.import.importingFrom.filter(id => id !== accountId),
          importSuccessFrom: [...state.import.importSuccessFrom, accountId],
          inProgress: false,
        },
      };
    }

    return {
      ...state,
      import: {
        ...state.import,
        status,
        inProgress: true,
      },
    };
  })
  .case(selectImportAccountId, (state, payload) => {
    return {
      ...state,
      importAccountId: payload.id,
      import: {
        ...state.import,
        accountId: payload.id,
      },
    };
  })
  .case(resetImportState, () => {
    return {
      ...initialState,
    };
  })
  .case(toggleUnassignedUsersModal, state => {
    return {
      ...state,
      showUnassignedUsersModal: !state.showUnassignedUsersModal,
    };
  });

export default withBatchSelectionsReducer(withXhrStatusReducer(reducer));
